import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { RemoveScroll } from "react-remove-scroll";
import globalTranslations from "../i18n/index.js";
import { getActiveSite } from "../selectors/sites.js";
import { selectTopLevelMainMenuItems } from "../slices/pages.js";
import { useAppSelector } from "../store/hooks.js";
import { createAppSelector } from "../store/utils.js";
import styles from "../stylesheets/components/header-sidebar.module.css";
import mainMenuStyles from "../stylesheets/components/main-menu.module.css";
import { ActionLinks, ColorScheme, Language } from "../types/index.js";
import { css } from "../utils/emotion.js";
import {
  getActionAndContactLinks,
  getActivePagePath,
  getFallbackLanguage,
  getTranslations,
} from "../utils/utils.js";
import HamburgerMenuList from "./HamburgerMenuList.js";
import HeaderSidebarIconNav from "./HeaderSidebarIconNav.js";
import Icon from "./Icon.js";
import LanguageMenu from "./LanguageMenu.js";

interface Props {
  scheme: ColorScheme;
  languageId: Language;
  isPreview: boolean;
  pageId: string;
  actionLinks: ActionLinks;
  whatsAppNumber: string | undefined;
}

const HeaderSidebar: FunctionComponent<Props> = ({
  scheme,
  languageId,
  actionLinks,
  whatsAppNumber,
  isPreview,
  pageId,
}) => {
  const pages = useAppSelector((state) => state.pages);
  const fallbackLanguageId = useAppSelector((state) =>
    getFallbackLanguage(getActiveSite(state.sites), languageId),
  );
  const navLinks = useAppSelector((state) =>
    selectNavLinks(state, languageId, actionLinks, whatsAppNumber),
  );
  const menuItems = useAppSelector((state) =>
    selectTopLevelMainMenuItems(
      state,
      languageId,
      isPreview,
      fallbackLanguageId,
    ),
  );
  const activePagePath = useAppSelector(
    (state) => getActivePagePath(pageId, state.pages.byParentId),
    shallowEqual,
  );

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const i18n = getTranslations(languageId, globalTranslations);

  return (
    <div
      className={ClassNames(
        styles.HeaderSidebar,
        { [styles["HeaderSidebar--preview"]]: isPreview },
        css({ backgroundColor: scheme.main.background }),
      )}
    >
      <div className={styles.HeaderSidebar__MenuButtonWrapper} ref={ref}>
        <button
          type="button"
          className={ClassNames(styles.HeaderSidebar__MenuButton, "Btn--bare")}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? i18n.menu.close : i18n.menu.open}
        >
          <Icon
            className={ClassNames(
              styles.HeaderSidebar__MenuIcon,
              css({ fill: scheme.main.text }),
            )}
            glyph="view-headline"
          />
        </button>
        {isMenuOpen && (
          <div
            className={ClassNames(
              styles.HeaderSidebar__MenuWrapper,
              mainMenuStyles.MainMenu__HeaderSidebar,
            )}
            onClick={(event) => {
              // Close menu when clicking on the wrapper but not in the menu
              event.currentTarget === event.target && setIsMenuOpen(false);
            }}
          >
            <RemoveScroll>
              <HamburgerMenuList
                className={styles.HeaderSidebar__Menu}
                scheme={scheme}
                isPreview={isPreview}
                pages={pages}
                languageId={languageId}
                topLevelMainMenuItems={menuItems}
                activePagePath={activePagePath}
                fallbackLanguageId={fallbackLanguageId}
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                outerRef={ref}
                showMenuCaption={true}
              />
            </RemoveScroll>
          </div>
        )}
      </div>

      <HeaderSidebarIconNav
        isPreview={isPreview}
        navLinks={navLinks}
        scheme={scheme}
        hoverTextDirection="right"
      />

      <LanguageMenu
        isPreview={isPreview}
        languageId={languageId}
        scheme={scheme}
        pageId={pageId}
        variant="horizontal"
        switchToSelectLimit={1}
        className={styles.LanguageMenu}
      />
    </div>
  );
};

const selectNavLinks = createAppSelector(
  [
    (state) => state.accommodation,
    (_, languageId: Language) => languageId,
    (_, _languageId: Language, actionLinks: ActionLinks) => actionLinks,
    (
      _,
      _languageId: Language,
      _actionLinks: ActionLinks,
      whatsAppNumber: string | undefined,
    ) => whatsAppNumber,
  ],
  (accommodation, languageId, actionLinks, whatsAppNumber) =>
    getActionAndContactLinks(
      accommodation[languageId],
      actionLinks,
      languageId,
      whatsAppNumber,
    ),
);

export default HeaderSidebar;
